/* Kubota Collection Grid — custom sort dropdown + AJAX sort swap.
   The trigger uses native <details>/<summary> for keyboard + click toggle.
   This module adds:
     - click-outside close, Escape close, aria-expanded sync
     - roving focus inside the open panel
     - AJAX option click: fetches the section via Shopify's section
       rendering API, swaps the grid + pagination in place, updates the
       URL with `history.pushState`, never scrolls. */
(function () {
  function getSortRoot(el) {
    return el.closest('[data-kcg-sort]');
  }

  function getSectionRoot(el) {
    return el.closest('.kubota-collection-grid');
  }

  function closeAll(exceptRoot) {
    document.querySelectorAll('[data-kcg-sort] [data-kcg-sort-details][open]').forEach((d) => {
      if (exceptRoot && d.closest('[data-kcg-sort]') === exceptRoot) return;
      d.removeAttribute('open');
    });
  }

  function syncAria(details) {
    const summary = details.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');
    }
  }

  function shortLabel(label) {
    if (!label) return '';
    return label.split(',')[0].trim();
  }

  function updateTrigger(root, label, showFull) {
    const current = root.querySelector('[data-kcg-sort-current]');
    if (!current) return;
    current.textContent = showFull ? label : shortLabel(label);
  }

  function updateSelectedState(root, selectedValue) {
    root.querySelectorAll('[data-kcg-sort-option]').forEach((opt) => {
      const isSel = opt.dataset.kcgSortValue === selectedValue;
      opt.setAttribute('aria-selected', isSel ? 'true' : 'false');
      opt.classList.toggle('is-selected', isSel);
    });
  }

  // Swap the grid HTML in place with the section's freshly-rendered markup
  // for the new sort key. Uses the Shopify Section Rendering API so the
  // response is only the section, not the whole page.
  function ajaxSort(section, optionEl) {
    const root = section;
    const sortValue = optionEl.dataset.kcgSortValue;
    const sortLabel = optionEl.querySelector('.kubota-collection-grid__sort-option-label')?.textContent?.trim() || sortValue;
    const sectionId = root.dataset.sectionId;
    if (!sectionId) {
      // Fallback: native navigate if we can't AJAX.
      window.location.href = optionEl.href;
      return;
    }

    const pathOnly = optionEl.getAttribute('href').split('?')[0];
    const url = new URL(optionEl.href, window.location.origin);
    url.searchParams.set('sort_by', sortValue);

    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', sectionId);

    // Mark loading state so callers can hook a spinner if needed.
    root.dataset.kcgLoading = '1';

    fetch(fetchUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((res) => {
        if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = doc.querySelector('.kubota-collection-grid');
        if (!incoming) throw new Error('Section markup not found in response');

        // Swap grid + pagination. The bar (count + sort dropdown) stays
        // put so the open dropdown isn't yanked from under the user.
        const incomingGrid = incoming.querySelector('.kubota-collection-grid__grid');
        const incomingPagination = incoming.querySelector('.kubota-collection-grid__pagination');
        const incomingEmpty = incoming.querySelector('.kubota-collection-grid__empty');
        const incomingCount = incoming.querySelector('.kubota-collection-grid__count');

        const currentGrid = root.querySelector('.kubota-collection-grid__grid');
        const currentPagination = root.querySelector('.kubota-collection-grid__pagination');
        const currentEmpty = root.querySelector('.kubota-collection-grid__empty');
        const currentCount = root.querySelector('.kubota-collection-grid__count');

        if (incomingGrid && currentGrid) {
          currentGrid.replaceWith(incomingGrid);
        } else if (incomingGrid && !currentGrid && currentEmpty) {
          currentEmpty.replaceWith(incomingGrid);
        } else if (incomingEmpty && !currentEmpty && currentGrid) {
          currentGrid.replaceWith(incomingEmpty);
          // Also drop the old pagination since we're now empty.
          if (currentPagination) currentPagination.remove();
        }

        if (incomingPagination) {
          if (currentPagination) {
            currentPagination.replaceWith(incomingPagination);
          } else {
            (root.querySelector('.kubota-collection-grid__grid') || root).appendChild(incomingPagination);
          }
        } else if (currentPagination) {
          currentPagination.remove();
        }

        if (incomingCount && currentCount) {
          currentCount.textContent = incomingCount.textContent;
        }

        // Push new URL into history without scrolling.
        const cleanUrl = new URL(pathOnly, window.location.origin);
        cleanUrl.search = url.search; // keep sort_by + any other params
        cleanUrl.searchParams.delete('section_id');
        history.pushState({ kcgSort: sortValue }, '', cleanUrl.toString());

        // Update trigger label + selected-state in the existing dropdown.
        const sortRoot = root.querySelector('[data-kcg-sort]');
        if (sortRoot) {
          const showFull = sortRoot.dataset.kcgSortShowFull === '1';
          updateTrigger(sortRoot, sortLabel, showFull);
          updateSelectedState(sortRoot, sortValue);
          const details = sortRoot.querySelector('[data-kcg-sort-details]');
          if (details) details.removeAttribute('open');
        }

        // Re-bind any newly-injected dropdowns + cards.
        document.dispatchEvent(new CustomEvent('kcg:swap', { detail: { root, sortValue } }));
      })
      .catch((err) => {
        // Fallback to native nav so the user still gets the right page.
        console.warn('[kcg] ajax sort failed, falling back to nav:', err);
        window.location.href = optionEl.href;
      })
      .finally(() => {
        delete root.dataset.kcgLoading;
      });
  }

  function bind(root) {
    if (root.dataset.kcgSortBound === '1') return;
    root.dataset.kcgSortBound = '1';
    const details = root.querySelector('[data-kcg-sort-details]');
    if (!details) return;

    details.addEventListener('toggle', () => {
      syncAria(details);
      if (details.open) closeAll(root);
    });
    syncAria(details);

    const options = root.querySelectorAll('[data-kcg-sort-option]');
    options.forEach((opt, i) => {
      // AJAX swap on click — intercept and run the in-place swap.
      opt.addEventListener('click', (e) => {
        // Allow new-tab / cmd-click / middle-click to navigate normally.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
        e.preventDefault();
        const section = getSectionRoot(opt);
        if (section) ajaxSort(section, opt);
      });

      opt.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          options[(i + 1) % options.length].focus();
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
          e.preventDefault();
          options[(i - 1 + options.length) % options.length].focus();
        } else if (e.key === 'Home') {
          e.preventDefault();
          options[0].focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          options[options.length - 1].focus();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          details.removeAttribute('open');
          details.querySelector('summary')?.focus();
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          opt.click();
        }
      });
    });

    const summary = details.querySelector('summary');
    if (summary) {
      summary.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && details.open) {
          e.preventDefault();
          details.removeAttribute('open');
        } else if (e.key === 'ArrowDown' && !details.open) {
          e.preventDefault();
          details.setAttribute('open', '');
          root.querySelector('[data-kcg-sort-option]')?.focus();
        }
      });
    }
  }

  document.addEventListener('click', (e) => {
    const inside = e.target.closest && e.target.closest('[data-kcg-sort]');
    if (inside) return;
    closeAll(null);
  });

  // Restore selected sort on browser back/forward.
  window.addEventListener('popstate', () => {
    const params = new URLSearchParams(window.location.search);
    const sortValue = params.get('sort_by');
    if (!sortValue) return;
    document.querySelectorAll('.kubota-collection-grid').forEach((section) => {
      const opt = section.querySelector(`[data-kcg-sort-value="${CSS.escape(sortValue)}"]`);
      if (opt) ajaxSort(section, opt);
    });
  });

  function init() {
    document.querySelectorAll('[data-kcg-sort]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    e.target?.querySelectorAll('[data-kcg-sort]').forEach(bind);
  });
})();

/* ====================================================================
   Pagination dots wiring (mobile slider only).
   Dot DOM lives in liquid; this module just keeps the active dot in
   sync with the currently-snapped cell, and scrolls a cell into view
   when its dot is clicked. CSS handles visibility — JS just observes.
   ==================================================================== */
(function () {
  const sectionObservers = new WeakMap();

  function bindDots(section) {
    const bleed = section.querySelector('.kubota-collection-grid__bleed');
    const dots = Array.from(section.querySelectorAll('[data-kcg-dot-target]'));
    let cells = Array.from(section.querySelectorAll('.kubota-collection-grid__cell'));

    if (!bleed || cells.length < 2 || dots.length === 0) return;

    // Tear down any prior observer on this section before re-binding.
    const prev = sectionObservers.get(section);
    if (prev) prev.disconnect();

    function setActive(index) {
      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    dots.forEach((dot) => {
      const i = parseInt(dot.dataset.kcgDotTarget, 10);
      dot.addEventListener('click', () => {
        if (Number.isNaN(i) || !cells[i]) return;
        cells[i].scrollIntoView({
          behavior: 'smooth',
          inline: 'start',
          block: 'nearest',
        });
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the highest intersection ratio that is
        // intersecting — that's the cell visually snapped at the start.
        let best = null;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
        });
        if (!best) return;
        const cellIndex = cells.indexOf(best.target);
        if (cellIndex < 0) return;
        setActive(cellIndex);
      },
      { root: bleed, threshold: [0.5, 0.75, 0.95] }
    );

    cells.forEach((cell) => observer.observe(cell));
    sectionObservers.set(section, observer);
  }

  function initAll() {
    document.querySelectorAll('.kubota-collection-grid').forEach(bindDots);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  // After an AJAX sort swap the grid is replaced — re-observe the new
  // cells. The dot DOM count is unchanged (sort doesn't change product
  // count) so we only need to rebind the IntersectionObserver targets.
  document.addEventListener('kcg:swap', (e) => {
    const root = e.detail?.root;
    if (root) bindDots(root);
  });

  document.addEventListener('shopify:section:load', (e) => {
    e.target?.querySelectorAll('.kubota-collection-grid').forEach(bindDots);
  });
})();
